/**
 * The front desk.
 *
 * Built for somebody standing up, on a tablet, with a queue in front of them.
 * That shapes two decisions: the scanner is always live rather than behind a
 * button, and the result of a scan is a large, unmissable panel rather than a
 * toast that fades while they are looking at the next person.
 *
 * The warnings are the point of the screen. An outstanding waiver is worth
 * mentioning at exactly one moment — when the member is standing there — and
 * this is the only place in the system where that moment is captured.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHECKIN_METHOD_LABEL, type CheckIn, type CheckinResult, type CheckinStation,
  type Member,
} from '@lcc/shared';
import { api } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { PageHeader } from '../App';
import {
  Card, Empty, ErrorNote, Loading, Modal, Pill, Stat, Tabs, TextField,
  errorMessage, useToast,
} from '../components/ui';

type Tab = 'desk' | 'log' | 'stations';

export function CheckInPage() {
  const [tab, setTab] = useState<Tab>('desk');
  const today = useQuery({
    queryKey: ['checkin', 'today'],
    queryFn: () => api.get<{ data: CheckIn[]; summary: { total: number; needingAttention: number } }>(
      '/checkin/today',
    ),
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        title="Check in"
        sub="Scan a member's code, or check somebody in by hand"
      />
      <div className="content stack">
        <div className="grid grid--4">
          <Stat label="In today" value={today.data?.summary.total ?? '—'} />
          <Stat
            label="Worth a word"
            value={today.data?.summary.needingAttention ?? '—'}
            note="Waiver outstanding or membership lapsed"
            alert={(today.data?.summary.needingAttention ?? 0) > 0}
          />
        </div>

        <Tabs
          tabs={[
            { id: 'desk', label: 'The desk' },
            { id: 'log', label: 'Today', count: today.data?.data.length },
            { id: 'stations', label: 'Door codes' },
          ] as const}
          active={tab}
          onChange={setTab}
        />

        {tab === 'desk' && <Desk />}
        {tab === 'log' && <TodayLog query={today} />}
        {tab === 'stations' && <Stations />}
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- the desk */

function Desk() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [result, setResult] = useState<CheckinResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const scan = useMutation({
    mutationFn: (payload: string) =>
      api.post<{ data: CheckinResult }>('/checkin/scan', { payload }).then((r) => r.data),
    onSuccess: (data) => {
      setFailure(null);
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ['checkin', 'today'] });
    },
    onError: (err) => {
      setResult(null);
      setFailure(errorMessage(err));
    },
  });

  const manual = useMutation({
    mutationFn: (memberId: string) =>
      api.post<{ data: CheckinResult }>('/checkin/manual', { memberId }).then((r) => r.data),
    onSuccess: (data) => {
      setResult(data);
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['checkin', 'today'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className="grid grid--2">
      <Card title="Scan">
        <Scanner onScan={(payload) => scan.mutate(payload)} busy={scan.isPending} />
      </Card>

      <div style={{ display: 'grid', gap: '1rem', alignContent: 'start' }}>
        {failure && (
          <div className="banner banner--error" role="alert">
            <strong>{failure}</strong>
          </div>
        )}
        {result && <ScanResult result={result} />}
        {!result && !failure && (
          <Card title="Nothing scanned yet">
            <p className="small">
              Point the camera at the code in a member&apos;s app. The result
              appears here.
            </p>
          </Card>
        )}
        <Card title="No phone?">
          <MemberPicker
            onPick={(m) => manual.mutate(m.id)}
            busy={manual.isPending}
          />
        </Card>
      </div>
    </div>
  );
}

/**
 * The camera.
 *
 * Uses the browser's own BarcodeDetector where it exists — Chrome and Edge on
 * the kind of tablet a desk actually has — and falls back to typing the code
 * in by hand everywhere else, rather than shipping a QR decoding library for
 * a case that mostly will not arise. Safari is the fallback today.
 */
function Scanner({ onScan, busy }: { onScan: (payload: string) => void; busy: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);
  const [typed, setTyped] = useState('');
  // The camera reads the same code many times a second; without this one
  // member would be scanned twenty times while they put their phone away.
  const lastSent = useRef<{ value: string; at: number }>({ value: '', at: 0 });

  const send = useCallback((value: string) => {
    const now = Date.now();
    if (value === lastSent.current.value && now - lastSent.current.at < 4000) return;
    lastSent.current = { value, at: now };
    onScan(value);
  }, [onScan]);

  useEffect(() => {
    const Detector = (globalThis as { BarcodeDetector?: new (o: { formats: string[] }) => {
      detect: (s: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
    } }).BarcodeDetector;

    if (!Detector) { setSupported(false); return; }

    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const detector = new Detector({ formats: ['qr_code'] });
        timer = window.setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes[0]?.rawValue) send(codes[0].rawValue);
          } catch {
            // A single failed frame is not worth reporting; the next one will do.
          }
        }, 400);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The camera could not be started.');
      }
    })();

    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [send]);

  return (
    <div style={{ display: 'grid', gap: '.75rem' }}>
      {supported && !error && (
        <video
          ref={videoRef}
          muted
          playsInline
          style={{
            width: '100%', borderRadius: 12, background: '#071120',
            aspectRatio: '4 / 3', objectFit: 'cover',
            outline: busy ? '3px solid #3ba4dd' : 'none',
          }}
        />
      )}
      {!supported && (
        <div className="banner">
          This browser cannot use the camera to read QR codes. Chrome or Edge
          can; otherwise type the code in below.
        </div>
      )}
      {error && <div className="banner banner--error">{error}</div>}

      <form
        onSubmit={(e) => { e.preventDefault(); if (typed.trim()) { send(typed.trim()); setTyped(''); } }}
      >
        <TextField
          label="Or type the code"
          value={typed}
          onChange={setTyped}
          placeholder="LCC1.…"
          hint="For a scanner that will not read, or a code read down the phone."
        />
        <button className="btn" type="submit" disabled={!typed.trim() || busy}>
          Check in
        </button>
      </form>
    </div>
  );
}

function ScanResult({ result }: { result: CheckinResult }) {
  const { member, checkIn, warnings, duplicate } = result;
  return (
    <section className={warnings.length ? 'card stat--alert' : 'card'}>
      <div className="card__body">
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>{member.fullName}</h2>
            <p className="small" style={{ margin: '.15rem 0 0' }}>
              {duplicate ? 'Already checked in' : 'Checked in'}
              {checkIn.drawLabel ? ` · ${checkIn.drawLabel}` : ''}
            </p>
          </div>
          <div className="spacer" />
          {warnings.length === 0
            ? <Pill tone="good">All clear</Pill>
            : <Pill tone="warn">{warnings.length} to mention</Pill>}
        </div>

        {warnings.length > 0 && (
          <ul style={{ margin: '.75rem 0 0', paddingLeft: '1.1rem' }}>
            {warnings.map((w) => <li key={w.code}>{w.message}</li>)}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Type-ahead over members, for checking somebody in without a phone. */
function MemberPicker({ onPick, busy }: { onPick: (m: Member) => void; busy: boolean }) {
  const [q, setQ] = useState('');
  const search = useDebounced(q);

  const results = useQuery({
    queryKey: ['members', 'search', search],
    queryFn: () => api.get<{ data: Member[] }>(
      `/members?q=${encodeURIComponent(search)}&perPage=8`,
    ).then((r) => r.data),
    enabled: search.trim().length >= 2,
  });

  return (
    <div>
      <TextField label="Find a member" value={q} onChange={setQ} placeholder="Surname" />
      {results.data?.map((m) => (
        <button
          key={m.id}
          type="button"
          className="btn btn--sm btn--block"
          style={{ marginBottom: '.35rem', textAlign: 'left' }}
          disabled={busy}
          onClick={() => { onPick(m); setQ(''); }}
        >
          {m.fullName}
        </button>
      ))}
      {search.trim().length >= 2 && results.data?.length === 0 && (
        <p className="small">Nobody by that name.</p>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- the log */

function TodayLog({ query }: {
  query: { isLoading: boolean; error: unknown; data?: { data: CheckIn[] } };
}) {
  if (query.isLoading) return <Loading what="Reading today" />;
  if (query.error) return <ErrorNote error={query.error} />;
  const rows = query.data?.data ?? [];
  if (rows.length === 0) return <Empty title="Nobody in yet today" />;

  return (
    <Card flush>
      <table className="table">
        <thead>
          <tr><th>Member</th><th>Time</th><th>How</th><th>For</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.memberName}</td>
              <td className="mono">
                {new Date(r.occurredAt).toLocaleTimeString([], {
                  hour: '2-digit', minute: '2-digit',
                })}
              </td>
              <td>{CHECKIN_METHOD_LABEL[r.method]}</td>
              <td>{r.drawLabel ?? r.stationName ?? '—'}</td>
              <td>
                {r.wasCleared === false && <Pill tone="warn">waiver</Pill>}
                {r.membershipOk === false && <Pill tone="bad">membership</Pill>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* --------------------------------------------------------------- stations */

function Stations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');

  const stations = useQuery({
    queryKey: ['checkin', 'stations'],
    queryFn: () => api.get<{ data: CheckinStation[] }>('/checkin/stations').then((r) => r.data),
  });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['checkin', 'stations'] });
    setAdding(false);
    setName('');
    setLocation('');
  };

  const create = useMutation({
    mutationFn: () => api.post('/checkin/stations', { name, location: location || null }),
    onSuccess: () => { toast.success('Door code created. Print it and put it up.'); done(); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.post(`/checkin/stations/${id}/rotate`),
    onSuccess: () => {
      toast.success('New code issued. The old poster no longer works — reprint it.');
      done();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (stations.isLoading) return <Loading what="Reading the door codes" />;
  if (stations.error) return <ErrorNote error={stations.error} />;

  return (
    <>
      <Card
        title="Door codes"
        actions={<button className="btn btn--sm" type="button" onClick={() => setAdding(true)}>
          Add one
        </button>}
        flush
      >
        {stations.data?.length
          ? (
            <table className="table">
              <thead>
                <tr><th>Name</th><th>Where</th><th>Link to print</th><th /></tr>
              </thead>
              <tbody>
                {stations.data.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.name}{' '}
                      {!s.isActive && <Pill tone="bad">retired</Pill>}
                      {s.requiresDraw && <Pill tone="warn">draw times only</Pill>}
                    </td>
                    <td>{s.location ?? '—'}</td>
                    <td className="mono small">{s.url}</td>
                    <td>
                      <button
                        className="btn btn--sm"
                        type="button"
                        onClick={() => {
                          if (window.confirm(
                            'Issue a new code? The printed poster will stop working '
                            + 'and must be reprinted.',
                          )) rotate.mutate(s.id);
                        }}
                      >
                        New code
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
          : (
            <div className="card__body">
              <Empty title="No door codes yet">
                A door code is a QR you print and put up by an entrance. A member
                scans it in the app to check themselves in when nobody is on the desk.
              </Empty>
            </div>
          )}
      </Card>

      {adding && (
        <Modal
          title="New door code"
          onClose={() => setAdding(false)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setAdding(false)}>Cancel</button>
              <button
                className="btn btn--primary"
                type="button"
                disabled={!name.trim() || create.isPending}
                onClick={() => create.mutate()}
              >
                Create
              </button>
            </>
          }
        >
          <TextField label="Name" value={name} onChange={setName} placeholder="Front entrance" />
          <TextField
            label="Where it is"
            value={location}
            onChange={setLocation}
            placeholder="By the trophy case"
            hint="Only so the office remembers which poster is which."
          />
        </Modal>
      )}
    </>
  );
}
